<template>
  <div class="w-full" :key="order.updated_at">
    <h1 class="mb-8 font-bold text-3xl">
      <inertia-link class="text-indigo-400 hover:text-indigo-600" :href="route('customer.orders')">Customer Orders</inertia-link>
      <span class="text-indigo-400 font-medium">/</span>
      Order #{{ order.id }}
    </h1>

    <div class="flex flex-col xl:flex-row xl:-mx-4">
      <!-- start left col -->
      <div class="w-full">
        <div class="bg-white shadow overflow-hidden sm:rounded-lg">
          <div class="px-4 py-5 border-b border-gray-200 sm:px-6">
            <div class="flex justify-between items-center">

              <div>
                <a :href="route('customers.edit', order.customer_account.id)" target="_blank">
                  <h3 class="text-lg leading-6 font-medium text-gray-900">
                    {{ order.customer_account.name }}
                  </h3>
                </a>
              </div>

              <span>
                <span class="inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium leading-5 bg-pink-100 text-pink-800" v-if="order.customer_account.credit_customer">CREDIT CUSTOMER</span>
                <span class="text-sm leading-5 text-gray-500">Status: </span>
                <span class="uppercase text-sm leading-5 text-gray-900">{{ order.status_label }}</span>
              </span>
            </div>

            <p class="flex justify-between mt-1 text-sm leading-5 text-gray-500">
              <span v-if="order.customer_account.personal_account">PERSONAL ACCOUNT</span>
              <span v-if="order.customer_account.tin_number">TIN: {{ order.customer_account.tin_number }}</span>
              <span>Date: {{ order.created_at | localDateTime }}</span>
            </p>


          </div>

          <div class="px-4 py-5 sm:px-6">
            <div>
              <div role="group">
                <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-baseline">
                  <div>
                    <div class="text-base leading-6 font-medium text-gray-900 sm:text-sm sm:leading-5 sm:text-gray-700">
                      Customer Reference
                    </div>
                  </div>
                  <div class="mt-4 sm:mt-0 sm:col-span-2">
                    <div class="max-w-lg">
                      <div class="flex justify-between items-center">
                        {{ order.customer_reference ? order.customer_reference : '-' }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="mt-2">
              <div role="group">
                <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-baseline">
                  <div>
                    <div class="text-base leading-6 font-medium text-gray-900 sm:text-sm sm:leading-5 sm:text-gray-700">
                      Created By
                    </div>
                  </div>
                  <div class="mt-4 sm:mt-0 sm:col-span-2">
                    <div class="max-w-lg">
                      <div class="flex justify-between items-center">
                        <div>
                          <template v-if="order.created_by">
                            <div class="text-xs text-gray-500 mb-2">{{ order.created_by.customer ? 'Customer' : 'Staff' }}</div>
                            <div>{{ `${order.created_by.first_name} ${order.created_by.last_name}` }}</div>
                          </template>
                          <template v-else>
                              <div class="text-xs text-gray-500 mb-2">Deleted User</div>
                          </template>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="order.quotation_ref" class="mt-8 border-t border-gray-200 pt-8 sm:mt-5">
              <div role="group">
                <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-baseline">
                  <div>
                    <div class="text-base leading-6 font-medium text-gray-900 sm:text-sm sm:leading-5 sm:text-gray-700">
                      Quotation
                    </div>
                  </div>
                  <div class="mt-4 sm:mt-0 sm:col-span-2">
                    <div class="max-w-lg">
                      <div class="flex justify-between items-center">
                        <div>
                          <div class="text-xs text-gray-500 mb-2">{{ order.quotation_ref }}</div>
                          <div>{{ order.quotation_amount }}</div>
                          <div class="my-4" >
                              <a v-if="order.quotation_file_path" :href="route('customer.orders.download-file', {customerOrder: order.id, file: 'quotation', preview: 'preview' })" target="_blank" class="btn-primary px-2.5 py-1.5">Download</a>
                              <inertia-link v-if="canAdmin('sync-quotation') && order.quotation_ref" :href="route('customer.orders.sync-quotation', order.id)" class="btn-primary px-2.5 py-1.5 ml-2">Sync Quotation</inertia-link>
                          </div>

                          <div class="mt-2 text-sm">{{ order.quotation_customer_approved === true ? 'Customer has accepted quotation' : 'Customer has not accepted the quotation yet.' }}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="order.invoice_ref" class="mt-8 border-t border-gray-200 pt-8 sm:mt-5">
              <div role="group">
                <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-baseline">
                  <div>
                    <div class="text-base leading-6 font-medium text-gray-900 sm:text-sm sm:leading-5 sm:text-gray-700">
                      Invoice
                    </div>
                  </div>
                  <div class="mt-4 sm:mt-0 sm:col-span-2">
                    <div class="max-w-lg">
                      <div class="flex justify-between items-center">
                        <div>
                          <div class="text-xs text-gray-500 mb-2">{{ order.invoice_ref }}</div>
                          <div>{{ order.invoice_amount }}</div>
                          <div class="my-4"><a :href="route('customer.orders.download-file', {customerOrder: order.id, file: 'invoice',  preview: 'preview'})" target="_blank" class="btn-primary px-2.5 py-1.5">Download</a></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="my-3 border-green-400 border-2 p-4 text-green-700" v-if="order.status === 'review' && order.review_data.length === 0">
              <h3 class="text-lg leading-5">REVIEW STATUS</h3>
              <p>No issues identified. </p>
            </div>

            <div class="my-3 border-yellow-400 border-2 p-4 text-orange-700" v-if="order.status === 'review' && order.review_data.length > 0">
              <h3 class="text-lg leading-5">REVIEW STATUS</h3>
              <div class="divide-y divide-yellow-600 divide-dashed">
                <div v-for="(review, indx) in order.review_data" :key="indx" class="py-2">
                  <h4 class="text-sm">{{ review.message }}</h4>
                  <div v-if="review.type !== 'message_only'">

                    <div v-if="review.type === 'item_qty_mismatch'" class="text-yellow-700 text-xs pl-2 my-1">
                      <ul v-for="(issues, itemCode) in review.item_codes">
                        <li><b>{{ itemCode }}</b>
                          <span v-for="issue in issues" :key="issue.unit + itemCode" class="mx-3">
                          {{ issue.unit}} : Qty {{ issue.qty }}
                        </span>
                        </li>
                      </ul>
                    </div>

                    <div v-if="review.type === 'not_in_order' || review.type === 'not_in_invoice'" class="text-yellow-700 text-xs pl-2 my-1">
                      <div class="grid divide-x divide-yellow-600 grid-cols-12">
                        <div v-for="itemCode in review.item_codes" :key="itemCode"> <b>{{ itemCode }}</b></div>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </div>

            <div class="sm:border-t sm:border-gray-200 sm:pt-5 mt-4">
              <div role="group">
                <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-baseline">
                  <div>
                    <div class="text-base leading-6 font-medium text-gray-900 sm:text-sm sm:leading-5 sm:text-gray-700">
                      Notification Contact Number
                    </div>
                  </div>
                  <div class="mt-4 sm:mt-0 sm:col-span-2">
                    <div class="max-w-lg sm:max-w-xs flex items-center gap-2">
                      <text-input :disabled="isLocked" :errors="$page.props.errors.customer_contact_number" v-model="form.customer_contact_number" />
                      <a v-if="form.customer_contact_number" :href="`tel:${form.customer_contact_number}`" class="text-sm leading-5 text-gray-500 hover:text-gray-900 p-3">
                        <Icon name="phone" class="h-4 w-4 mx-1" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- -->
            <div class="mt-8 border-t border-gray-200 pt-8 sm:mt-5">
              <div>
                <h3 class="text-lg leading-6 font-medium text-gray-900">
                  Customer Handover/Delivery Details
                </h3>
              </div>
              <div class="mt-6 sm:mt-5">
                <div class="sm:border-t sm:border-gray-200 sm:pt-5">
                  <div role="group">
                    <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-baseline">
                      <div>
                        <div class="text-base leading-6 font-medium text-gray-900 sm:text-sm sm:leading-5 sm:text-gray-700">
                          Delivery Method
                        </div>
                      </div>
                      <div class="mt-4 sm:mt-0 sm:col-span-2">
                        <div class="max-w-xs">
                          <select-input :disabled="isLocked" v-model="form.customer_to_collect">
                            <option :value="false">To be Delivered</option>
                            <option :value="true">Collection by Customer</option>
                          </select-input>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <template v-if="!form.customer_to_collect">

                  <div class="sm:border-t sm:border-gray-200 sm:pt-5 mt-4">
                    <div role="group">
                      <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-baseline">
                        <div>
                          <div class="text-base leading-6 font-medium text-gray-900 sm:text-sm sm:leading-5 sm:text-gray-700">
                            Location
                          </div>
                        </div>
                        <div class="mt-4 sm:mt-0 sm:col-span-2">
                          <div class="max-w-lg">
                            <select-input :disabled="isLocked" :errors="$page.props.errors.delivery_location" v-model="form.delivery_location">
                              <option v-for="(label, value) in locations" :value="value">{{ label }}</option>
                            </select-input>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div v-if="form.delivery_location !== 'island'" class="sm:border-t sm:border-gray-200 sm:pt-5 mt-4">
                    <div role="group">
                      <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-baseline">
                        <div>
                          <div class="text-base leading-6 font-medium text-gray-900 sm:text-sm sm:leading-5 sm:text-gray-700">
                            Address
                          </div>
                        </div>
                        <div class="mt-4 sm:mt-0 sm:col-span-2">
                          <div class="max-w-lg">
                            <textarea-input :disabled="isLocked" :errors="$page.props.errors.delivery_address" v-model="form.delivery_address"></textarea-input>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <template v-else>


                    <div class="sm:border-t sm:border-gray-200 sm:pt-5 mt-4">
                      <div role="group">
                        <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-baseline">
                          <div>
                            <div class="text-base leading-6 font-medium text-gray-900 sm:text-sm sm:leading-5 sm:text-gray-700">
                              Vessel Name
                            </div>
                          </div>
                          <div class="mt-4 sm:mt-0 sm:col-span-2">
<!--                            <div class="max-w-lg">-->
<!--                              <text-input :disabled="isLocked" :errors="$page.props.errors.vessel" v-model="form.vessel" />-->
<!--                            </div>-->

                            <delivery-to-boat :disabled="isLocked" @change="updateSelectedBoatID" :boatId="form.boat_id" :boat-name="form.vessel" :boat-location="form.vessel_location" :errors="$page.props.errors.vessel"></delivery-to-boat>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div class="sm:border-t sm:border-gray-200 sm:pt-5 mt-4">
                      <div role="group">
                        <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-baseline">
                          <div>
                            <div class="text-base leading-6 font-medium text-gray-900 sm:text-sm sm:leading-5 sm:text-gray-700">
                              Vessel Location
                            </div>
                          </div>
                          <div class="mt-4 sm:mt-0 sm:col-span-2">
                            <div class="max-w-lg">
                              <select-input :disabled="isLocked || !canAdmin('update-orders')" :errors="$page.props.errors.vessel_location" v-model="form.vessel_location">
                                <option v-for="jetty in vesselLocations.island" :value="jetty">{{ jetty }}</option>
                              </select-input>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                  </template>
                </template>

                <div class="mt-6 sm:mt-5">
                  <div class="sm:border-t sm:border-gray-200 sm:pt-5">
                    <div role="group">
                      <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-baseline">
                        <div>
                          <div class="text-base leading-6 font-medium text-gray-900 sm:text-sm sm:leading-5 sm:text-gray-700">
                            Delivery/Handover Date
                          </div>
                        </div>
                        <div class="mt-4 sm:mt-0 sm:col-span-2">
                          <div class="max-w-xs">
                            <date-input v-model="form.customer_handover_date" :disabled="isLocked" :errors="$page.props.errors.customer_handover_date"></date-input>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="sm:border-t sm:border-gray-200 sm:pt-5 mt-4">
                  <div role="group">
                    <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-baseline">
                      <div>
                        <div class="text-base leading-6 font-medium text-gray-900 sm:text-sm sm:leading-5 sm:text-gray-700">
                          Delivery Contact Number
                        </div>
                      </div>
                      <div class="mt-4 sm:mt-0 sm:col-span-2">
                        <div class="max-w-lg flex items-center gap-2">
                          <text-input :disabled="isLocked" :errors="$page.props.errors.delivery_contact_number" v-model="form.delivery_contact_number" />
                          <a v-if="form.delivery_contact_number" :href="`tel:${form.delivery_contact_number}`" class="text-sm leading-5 text-gray-500 hover:text-gray-900 p-3">
                            <Icon name="phone" class="h-4 w-4 mx-1" />
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="sm:border-t sm:border-gray-200 sm:pt-5 mt-4">
                  <div role="group">
                    <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-baseline">
                      <div>
                        <div class="text-base leading-6 font-medium text-gray-900 sm:text-sm sm:leading-5 sm:text-gray-700">
                          Remarks
                        </div>
                      </div>
                      <div class="mt-4 sm:mt-0 sm:col-span-2">
                        <div class="max-w-lg">
                          <textarea-input :disabled="isLocked" v-model="form.remarks" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <!-- -->

            <!-- payment information -->
            <div class="mt-8 border-t border-gray-200 pt-8 sm:mt-5" v-if="order.advance_payment && order.quotation_ref">
              <div>
                <h3 class="text-lg leading-6 font-medium text-gray-900">
                  Payment Information
                </h3>
              </div>

              <div class="mt-6 sm:mt-5">
                <div class="sm:border-t sm:border-gray-200 sm:pt-5">
                  <div role="group">
                    <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-baseline">
                      <div>
                        <div class="text-base leading-6 font-medium text-gray-900 sm:text-sm sm:leading-5 sm:text-gray-700">
                          Payment Method
                        </div>
                      </div>
                      <div class="mt-4 sm:mt-0 sm:col-span-2">
                        <div class="max-w-xs">
                          <select-input :disabled="isLocked"  v-model="payment_method">
                            <option v-for="method in paymentMethods" :value="method">{{ method }}</option>
                          </select-input>
                        </div>
                        <div v-if="payment_method === 'MIB_FAISAPAY'" class="text-sm my-2">
                          Reference No: <span class="text-sm font-medium text-gray-700">{{ order.payment_reference }}</span>
                        </div>
                        <div v-if="payment_method === 'MIB_FAISAPAY' && order.payment_portal_posted_at === null">
                          <InertiaLink :href="route('customer.orders.mib-post-payment', [order.id])" class="text-sm text-blue-500 underline py-1">Post Payment to Portal</InertiaLink>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="payment_method === 'transfer'" class="mt-6 sm:mt-5">
                <div class="sm:border-t sm:border-gray-200 sm:pt-5">
                  <div role="group">
                    <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-baseline">
                      <div>
                        <div class="text-base leading-6 font-medium text-gray-900 sm:text-sm sm:leading-5 sm:text-gray-700">
                          Payment Slip
                        </div>
                      </div>
                      <div class="mt-4 sm:mt-0 sm:col-span-2">
                        <div class="max-w-xs">
                          <file-input :errors="$page.props.errors.payment_slip" v-model="payment_slip"></file-input>
                          <div class="mt-4" v-if="order.customer_payment_transfer_slip_image_path">
                            <a :href="route('customer.orders.download-file', {customerOrder: order.id, file: 'payment-slip'})" target="_blank" class="btn-primary px-2.5 py-1.5">Download</a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="mt-6 sm:mt-5" v-if="order.customer_payment_transfer_at">
                <div class="sm:border-t sm:border-gray-200 sm:pt-5">
                  <div role="group">
                    <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-baseline">
                      <div>
                        <div class="text-base leading-6 font-medium text-gray-900 sm:text-sm sm:leading-5 sm:text-gray-700">
                          Customer payment updated at
                        </div>
                      </div>
                      <div class="mt-4 sm:mt-0 sm:col-span-2">
                        <div class="max-w-xs">
                          {{ order.customer_payment_transfer_at | localDateTime }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="mt-6 sm:mt-5">
                <div class="sm:border-t sm:border-gray-200 sm:pt-5">
                  <div role="group">
                    <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-baseline">
                      <div>
                        <div class="text-base leading-6 font-medium text-gray-900 sm:text-sm sm:leading-5 sm:text-gray-700">
                          Payment Received at
                        </div>
                      </div>
                      <div class="mt-4 sm:mt-0 sm:col-span-2">
                        <div class="max-w-xs">
                          <date-input v-model="payment_received_at" :disabled="isLocked" :errors="$page.props.errors.payment_received_at"></date-input>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="m:pt-5 mt-4" v-if="!isLocked">
                <div role="group">
                  <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-baseline">
                    <div>
                      <div class="text-base leading-6 font-medium text-gray-900 sm:text-sm sm:leading-5 sm:text-gray-700">
                      </div>
                    </div>
                    <div class="mt-4 sm:mt-0 sm:col-span-2">
                      <div class="max-w-xs flex items-end">
                        <div>
                          <loading-button :loading="paymentUpdating" @click="updatePayment" class="btn-primary mb-1">Update Payment</loading-button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>
            <!-- end of payment information -->

            <div class="my-4">

              <div class="mt-2">
                <div class="w-full max-w-xs rounded-t border border-gray-200 flex items-center border-b-0">
                  <span class="bg-gray-50 px-2 py-2 border-r border-gray-300 uppercase text-sm">Price Group</span>
                  <span class="px-2 py-2 uppercase text-sm flex-1"  :class="{
                    'bg-blue-100 text-blue-800' : order.price_group === 'STAFF',
                    'bg-indigo-100 text-indigo-800' : order.price_group === 'RETAIL',
                    'bg-green-100 text-green-800 ' : order.price_group === 'WHOLESALE',
                  }">{{ order.price_group }}</span>
                </div>
              </div>

              <!-- order details table -->

              <div class="flex flex-col">
                <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                  <div class="align-middle inline-block min-w-full overflow-hidden sm rounded-lg rounded-tl-none border border-gray-200">
                    <table class="min-w-full">
                      <thead>
                        <tr>
                          <th class="px-2 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
                          </th>
                          <th class="px-2 w-full py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
                            Item / Details
                          </th>
                          <th class="px-2 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
                            Qty
                          </th>
                          <th class="px-2 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">Qty Approved</th>
                          <th class="px-2 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">Warehouse</th>
<!--                          <th class="px-2 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">Date</th>-->
                          <th class="px-2 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">Delivered</th>
                          <th class="px-2 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">Price</th>
                          <th class="px-2 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">Discount</th>
                          <th class="px-2 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                          <th class="px-2 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"></th>
                        </tr>
                      </thead>

                      <tbody class="bg-white">
                        <tr v-for="(item, index) in form.items" :key="index" class="p-0">
                          <td class="p-0 whitespace-nowrap border-b border-gray-200 text-sm font-medium text-gray-900">
                            <input type="checkbox" v-model="checkedItems[item.id]" class="mx-2 form-checkbox h-4 w-4 text-indigo-600 transition duration-150 ease-in-out" />
                          </td>
                          <td class="p-0 border-b border-gray-200 font-medium text-gray-900 flex items-center">
                            <div class="py-2 w-full leading-3" :class="{'text-red-500' : duplicateItems[item.item_code] }">
                              <span class="text-xs">{{ item.description }}</span>
                              <span v-if="item.free_item" class="text-xs text-green-500 uppercase text-md">Free</span>
                              <div class="text-xs text-gray-400">
                                {{ item.item_code }}
                              </div>
                              <div v-if="item.special_offer" class="text-xs italic text-gray-300">
                                <span class="font-bold">OFFER | </span> {{ item.special_offer.title }}
                              </div>
                            </div>

                          </td>
                          <td class="p-0  whitespace-nowrap border-b border-gray-200 text-sm text-gray-500">
                            <span class="mr-1">{{ item.quantity }}</span><span class="text-xs" :class="{'text-red-400': item.base_unit !== item.unit}">{{ item.unit }}</span>
                          </td>
                          <td class="p-0 whitespace-nowrap border-b border-gray-200 text-sm text-gray-500">
                            <div class="flex items-center">
                              <input type="number" class="form-number-text w-16 p-2 rounded bg-gray-100 text-center mr-2 border-0" :disabled="!can_update_order_items || isLocked || isItemLocked(item)" :min="0" v-model.number="item.quantity_approved" @change="evt => updateItemPrice(item, evt)"/>
                              <div>
                                <available-qty-badge v-if="available_qty.loaded" :item="available_qty.items[item.item_code]" :unit="item.unit" :qty="item.quantity_approved" />
                              </div>
                            </div>
                          </td>
                          <td class="p-0 whitespace-nowrap border-b border-gray-200 text-sm text-gray-500">
                            <div class="w-full flex-col items-stretch px-2 flex">
                              <select class="border-none" :errors="$page.props.errors[`items.${index}.warehouse_id`]" v-if="canChangeItemLocation(item)" v-model="item.warehouse_id" @change="fetchAvailableQty(item)">
                                <option v-for="(warehouse, windx) in getWarehouseListFor(item.item_code)" :value="warehouse.id" :key="windx">{{ warehouse.name }}</option>
                              </select>
                              <span v-else>
                                {{ getWarehouseListFor(item.item_code).find((wh) => wh.id === item.warehouse_id).name }}
                              </span>
                            </div>
                          </td>
<!--                          <td class="px-2whitespace-nowrap border-b border-gray-200 text-sm text-gray-500">-->
<!--                            <date-input v-model="item.handover_date" :disabled="isLocked || isItemLocked(item)" :errors="$page.props.errors[`items.${index}.handover_date`]"></date-input>-->
<!--                          </td>-->
                          <td class="px-2whitespace-nowrap border-b border-gray-200 text-sm text-gray-500 text-center">
                            <span v-if="item.quantity_delivered" class="rounded p-2 border">{{ item.quantity_delivered }}</span>
                            <span v-else class="rounded p-2 border">0</span>
                          </td>
                          <td class="px-2whitespace-nowrap border-b border-gray-200 text-sm text-gray-500 text-right pr-4">
                            <span v-if="item.unit_price">{{ item.unit_price.toLocaleString() }}</span>
                            <span v-else>
                              <span v-if="item.free_item">0</span>
                              <span v-else>N/A</span>
                            </span>
                          </td>
                          <td class="px-2whitespace-nowrap border-b border-gray-200 text-sm text-gray-500 text-right pr-4">
                            {{ item.discount_applied.toLocaleString() }}
                          </td>
                          <td class="px-2whitespace-nowrap border-b border-gray-200 text-sm text-gray-500 text-right">
                            <div v-if="!item.discount_total_price">{{ item.price_total.toLocaleString() }}</div>
                            <div v-else>
                              <div class="text-sm">{{ item.discount_total_price.toLocaleString() }}</div>
                              <div class="text-xs line-through text-gray-400">{{ item.price_total.toLocaleString() }}</div>
                            </div>
                          </td>
                          <td class="py-0 px-1 whitespace-nowrap border-b border-gray-200 text-sm text-gray-500">
                            <button v-if="item.completed_at" title="Delivered" class="block w-4 h-4 text-green-600" @click="$refs.orderfulfillmenet.showDetailsByOtpId(item.handovers[0].order_otp_id || null)"><icon name="tick"></icon></button>
                            <button v-else-if="item.handovers.length" title="In Progress" class="block w-4 h-4 text-orange-500"><icon name="tick"></icon></button>
                          </td>
                        </tr>
                        <tr class="bg-gray-100">
                          <th colspan="7" class="text-right">Total</th>
                          <th class="py-3 px-2 text-right" colspan="3"><span class="text-sm font-normal">MVR</span> {{ total_price.toLocaleString() }}</th>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <!-- end of order details table -->

              <dl class="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2 mt-4">
                <div class="sm:col-span-1">
                  <dt class="text-sm leading-5 font-medium text-gray-500">
                    Last Updated:
                  </dt>
                  <dd class="mt-1 text-sm leading-5 text-gray-900 uppercase">
                    {{ order.updated_at | localDateTime }}
                  </dd>
                </div>

                <div class="sm:col-span-2 flex sm:justify-end">
                  <span>
                    <button class="flex items-center bg-red-200 text-red-400 px-3 py-2 rounded mr-2" v-if="['approved', 'quoted', 'invoiced'].indexOf(order.status) != -1"  @click="revertStatusToOpen" >Revert Status</button>
                  </span>
                  <span v-if="canAdmin('reopen-cancelled-order') && order.status === 'cancelled'">
                    <button class="flex items-center bg-red-200 text-red-400 px-3 py-2 rounded mr-2" @click="revertStatusToOpen" >ReOpen Order</button>
                  </span>
                  <span v-if="canAdmin('remove-items-from-an-order') && !orderIsCompleted">
                    <button class="btn-primary mr-2" v-if="['open', 'approved', 'quoted', 'payment_process','review'].indexOf(order.status) != -1" :disabled="disableRemoveButton" @click="modals.removeItem = true" >Remove Selected</button>
                  </span>

                  <span v-if="['open', 'approved', 'quoted'].indexOf(order.status) != -1 && canAdmin('update-items-in-order')">
                    <button class="btn-primary mr-2" @click="openItemsUpdateModal" :disabled="disableRemoveButton" >Update Items Selected</button>
                  </span>
                  <span v-if="canAdmin('add-items-to-order')">
                    <button :disabled="!can_update_order_items" class="btn-primary mr-2" v-if="['open', 'approved', 'quoted', 'payment_process', 'review'].indexOf(order.status) != -1" @click="modals.addItem = true">Add Item</button>
                  </span>
                  <loading-button :disabled="isLocked"  :class="{'bg-gray-300 hover:bg-gray-200': isLocked}" :loading="orderUpdating" class="btn bg-green-400 px-3 rounded text-green-800 mx-4" @click="approveOrder" v-if="!order.approved_at && canAdmin('approve-orders')">Approve</loading-button>
                  <loading-button :loading="orderUpdating" v-if="canAdmin('update-orders') || canAdmin('update-order-item-location')" class="btn-primary" @click="updateOrder">Update Order</loading-button>
                </div>
              </dl>
            </div>
          </div>
        </div>

        <DeliveryNotes
          :delivery-notes="order.delivery_notes"
          :warehouse-list="warehouseList"
          :locations="locations"
          :vessel-locations="vesselLocations"
          :order="order"
          :getWarehouseListFor="getWarehouseListFor"
         />

        <OrderFulfillmentDetails :order-id="order.id" ref="orderfulfillmenet" :warehouses="warehouseList"/>

        <!-- order history panel -->
        <OrderHistoryOld :activities="order.activities" :is-super-admin="isSuperAdmin"/>
        <!-- end of order history panel -->
      </div>
      <!-- end of left col -->

      <!-- start right col -->
      <div class="w-full mt-8 xl:mt-0 xl:w-2/12 xl:px-4">
        <div class="block xl:grid xl:grid-cols-1 xl:w-56 gap-y-2">
<!--          <div v-if="canAdmin('warehouse-manager-role') && order.status === 'invoiced'">-->

<!--            <select class="px-2 py-2 border-2 border-primary-300 rounded-md bg-transparent" v-model="handover_wcode">-->
<!--              <option :value="null">All</option>-->
<!--              <option v-for="uwh in user_warehouses" :value='uwh.id'>{{ uwh.code }}</option>-->
<!--            </select>-->

<!--            <a :href="printOrderUrl(order)" target="_blank"-->
<!--               class="text-primary-500 hover:text-primary-800 border-primary-500 hover:bg-primary-100 rounded-md p-2 text-center border-2 ">-->
<!--              Handover Print-->
<!--            </a>-->
<!--          </div>-->

          <button class="btn-primary" v-if="canAdmin('confirm-quotation') && order.approved_at &&  order.status === 'approved' && order.quotation_file_path" @click="confirmQuotation">Confirm Quotation</button>
          <button class="btn-primary" v-if="canAdmin('create-quotation') && order.status === 'approved' && !order.quotation_ref" @click="createQuotation">Create Quotation</button>
          <button class="btn-primary" v-if="canAdmin('create-quotation') && order.quotation_ref" @click="createQuotation">Regenerate Quotation</button>
          <button class="btn-primary" v-if="canAdmin('create-quotation') && order.quotation_ref && !order.invoice_ref" @click="createCreditInvoice">Make Credit Invoice</button>
          <button class="btn-primary"
                  :disabled="(!order.approved_at && !enableQuoatingWhenNotApproved) || order.status === 'completed' || orderIsCompleted === true"
                  @click="modals.invoice = true">Update Invoice</button>
          <a class="btn-primary text-center" :href="route('customer.orders.invoice-sync', {customerOrder: order.id})">Sync Invoice</a>
          <a class="btn-primary text-center" :href="route('customer.orders.csv', {customerOrder: order.id})">Download CSV</a>

          <a v-if="order.status === 'review'" class="btn-indigo text-center" :href="route('customer.orders.force-to-invoiced', {customerOrder: order.id})">Force Update to Invoiced</a>
          <a v-if="order.status === 'review'" class="bg-gray-700 rounded btn-gray text-center" :href="route('customer.orders.force-to-quoted', {customerOrder: order.id})">Force Revert to Quoted</a>

          <loading-button :loading="unlocking" v-if="order.lock && order.status !== 'completed'" class="btn-primary justify-center" @click="unlockOrder">Unlock</loading-button>
          <button :disabled="order.status === 'invoiced' || order.status === 'completed'" class="btn-red" @click="modals.cancel = true">Cancel Order</button>
        </div>


        <!-- add item modal -->
        <modal v-if="canAdmin('add-items-to-order') && can_update_order_items && orderIsCompleted === false" size="md" :show="modals.addItem" @closeModal="modals.addItem = false">
          <AddItemModal :warehouses="warehouseList" :order-id="order.id" @closeModal="() => modals.addItem = false" @itemAdded="itemAddedToOrder"/>
        </modal>
        <!-- end of add item modal-->

        <!--update selected items modal -->
        <modal v-if="canAdmin('update-items-in-order') && can_update_order_items" size="lg" :show="modals.updateSelectedItems" @closeModal="dismissUpdateItemsModal">
          <div class="px-1 py-2 sm:p-2">
            <h3 class="text-lg leading-6 font-medium text-gray-900 mb-2">
              Update Items Selected
            </h3>

            <table class="min-w-full mt-2">
              <thead>
              <tr>
                <th class="px-2 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
                  Item Code
                </th>
                <th class="px-2 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
                  Unit
                </th>
                <th class="px-2 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
                  Qty Ordered
                </th>
                <th class="px-2 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
                  Unit Price
                </th>

                <th class="px-2 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
                  Total Discount
                </th>

                <th class="px-2 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
                  Total
                </th>
              </tr>
              </thead>
              <tbody class="bg-white">
              <tr v-for="selectedProductItem in updateItems" :key="`update-${selectedProductItem.id}`">
                <td class="px-2 py-4 whitespace-nowrap border-b border-gray-200 text-sm leading-5 font-medium text-gray-900">
                  <div>{{ selectedProductItem.description }}</div>
                  {{ selectedProductItem.item_code }}
                </td>
                <td class="px-2 py-4 whitespace-nowrap border-b border-gray-200 text-sm leading-5 text-gray-900">
                  {{ selectedProductItem.unit }}
                </td>
                <td class="px-2 py-4 whitespace-nowrap border-b border-gray-200 text-sm leading-5 text-gray-900">
                  <input type="number" class="form-number-text w-16 p-2 rounded bg-gray-100 text-center mr-2 border-0" :min="0" v-model.number="selectedProductItem.quantity_approved"/>
                </td>
                <td class="px-2 py-4 whitespace-nowrap border-b border-gray-200 text-sm leading-5 text-gray-900">
                  MVR
                  <input type="number" class="form-number-text w-16 p-2 rounded bg-gray-100 text-center mr-2 border-0" :min="0" v-model.number="selectedProductItem.unit_price"/>
                </td>

                <td class="px-2 py-4 whitespace-nowrap border-b border-gray-200 text-sm leading-5 text-gray-900">
                  MVR
                  <input type="number" class="form-number-text w-16 p-2 rounded bg-gray-100 text-center mr-2 border-0" :min="0" v-model.number="selectedProductItem.discount_applied"/>
                </td>

                <td class="px-2 py-4 whitespace-nowrap border-b border-gray-200 text-sm leading-5 text-gray-900">
                  {{ (selectedProductItem.quantity_approved * selectedProductItem.unit_price) - selectedProductItem.discount_applied }}
                </td>
              </tr>
              </tbody>
            </table>


            <div class="mt-4 flex justify-end">
              <button class="btn-primary" @click="handleItemsUpdatedInOrder">Update Items</button>
            </div>

          </div>
        </modal>
        <!-- end of modal -->

        <!-- quotation modal -->
        <modal key="quote-modal" :show="modals.quotation" @closeModal="closeQuotationModal">
          <form key="quote-form" @submit.prevent="updateQuote">
            <div>
              <h3 class="text-lg leading-6 font-medium text-gray-900">
                Quotation
              </h3>
            </div>
            <div class="mt-6 sm:mt-5">
              <div class="sm:border-t sm:border-gray-200 sm:pt-5">
                <div role="group">
                  <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-baseline">
                    <div>
                      <div class="text-base leading-6 font-medium text-gray-900 sm:text-sm sm:leading-5 sm:text-gray-700">
                        Reference
                      </div>
                    </div>
                    <div class="mt-4 sm:mt-0 sm:col-span-2">
                      <div class="max-w-xs">
                        <text-input :errors="$page.props.errors.quotation_ref" required v-model="quote.quotation_ref"></text-input>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="mt-4 sm:border-t sm:border-gray-200 sm:pt-5">
              <div role="group">
                <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-baseline">
                  <div>
                    <div class="text-base leading-6 font-medium text-gray-900 sm:text-sm sm:leading-5 sm:text-gray-700">
                      Amount
                    </div>
                  </div>
                  <div class="mt-4 sm:mt-0 sm:col-span-2">
                    <div class="max-w-xs">
                      <text-input type="number" required :errors="$page.props.errors.quotation_amount" v-model="quote.quotation_amount" step="0.01"></text-input>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="mt-4 sm:border-t sm:border-gray-200 sm:pt-5">
              <div role="group">
                <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-baseline">
                  <div>
                    <div class="text-base leading-6 font-medium text-gray-900 sm:text-sm sm:leading-5 sm:text-gray-700">
                      Attach Quotation
                    </div>
                  </div>
                  <div class="mt-4 sm:mt-0 sm:col-span-2">
                    <div class="max-w-xs">

                      <div v-if="order.quotation_file_path" class="mb-4">
                        <a :href="route('customer.orders.download-file', {customerOrder: this.order.id, file: 'quotation'})" target="_blank" class="btn-primary px-2.5 py-1.5">Download</a>
                      </div>

                      <file-input :errors="$page.props.errors.quotation_file" v-model="quote.quotation_file"></file-input>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="isCredit" class="mt-4 sm:border-t sm:border-gray-200 sm:pt-5">
              <div role="group">
                <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-baseline">
                  <div>
                    <div class="text-base leading-6 font-medium text-gray-900 sm:text-sm sm:leading-5 sm:text-gray-700">

                    </div>
                  </div>
                  <div class="mt-4 sm:mt-0 sm:col-span-2">
                    <div class="relative flex items-start">
                      <div class="absolute flex items-center h-5">
                        <input id="lock" v-model="quote.advance_payment" type="checkbox" class="form-checkbox h-4 w-4 text-indigo-600 transition duration-150 ease-in-out" />
                        <div v-if="$page.props.errors.advance_payment" class="form-error">{{ $page.props.errors.advance_payment[0] }}</div>
                      </div>
                      <div class="pl-7 text-sm leading-5">
                        <label for="lock" class="font-medium text-gray-700">Require advance payment</label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="mt-4 flex justify-end">
              <loading-button key="quote-submit" type="submit" :loading="updatingQuotation" class="btn-primary">Update</loading-button>
            </div>
          </form>

        </modal>
        <!-- end of quotation modal -->

        <!-- invoice modal -->
        <modal key="invoice-modal" :show="modals.invoice" @closeModal="closeInvoiceModal()">
          <form key="invoice-form" @submit.prevent="updateInvoice">
            <div>
              <h3 class="text-lg leading-6 font-medium text-gray-900">
                Invoice
              </h3>
            </div>
            <div class="mt-6 sm:mt-5">

              <div class="sm:border-t sm:border-gray-200 sm:pt-5">
                <div role="group">
                  <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-baseline">
                    <div>
                      <div class="text-base leading-6 font-medium text-gray-900 sm:text-sm sm:leading-5 sm:text-gray-700">
                        Customer Confirmation
                      </div>
                    </div>
                    <div class="mt-4 sm:mt-0 sm:col-span-2">
                      <div class="relative flex items-start">
                        <div class="absolute flex items-center h-5">
                          <input id="customer_acceptance" required v-model="invoice.quotation_customer_approved" type="checkbox" class="form-checkbox h-4 w-4 text-indigo-600 transition duration-150 ease-in-out" />
                          <div v-if="$page.props.errors.customer_to_collect" class="form-error">{{ $page.props.errors.customer_to_collect[0] }}</div>
                        </div>
                        <div class="pl-7 text-sm leading-5">
                          <label for="customer_acceptance" class="font-medium text-gray-700">Customer has accepted the quote.</label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="mt-4 sm:border-t sm:border-gray-200 sm:pt-5">
                <div role="group">
                  <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-baseline">
                    <div>
                      <div class="text-base leading-6 font-medium text-gray-900 sm:text-sm sm:leading-5 sm:text-gray-700">
                        Reference
                      </div>
                    </div>
                    <div class="mt-4 sm:mt-0 sm:col-span-2">
                      <div class="max-w-xs">
                        <text-input :errors="$page.props.errors.invoice_ref" required v-model="invoice.invoice_ref"></text-input>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="mt-4 sm:border-t sm:border-gray-200 sm:pt-5">
                <div role="group">
                  <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-baseline">
                    <div>
                      <div class="text-base leading-6 font-medium text-gray-900 sm:text-sm sm:leading-5 sm:text-gray-700">
                        Amount
                      </div>
                    </div>
                    <div class="mt-4 sm:mt-0 sm:col-span-2">
                      <div class="max-w-xs">
                        <text-input :errors="$page.props.errors.invoice_amount" required v-model="invoice.invoice_amount" step="0.01"></text-input>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="mt-4 sm:border-t sm:border-gray-200 sm:pt-5">
                <div role="group">
                  <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-baseline">
                    <div>
                      <div class="text-base leading-6 font-medium text-gray-900 sm:text-sm sm:leading-5 sm:text-gray-700">
                        Attach Invoice
                      </div>
                    </div>
                    <div class="mt-4 sm:mt-0 sm:col-span-2">
                      <div class="max-w-xs">

                        <div v-if="order.invoice_file_path" class="mb-4">
                          <a :href="route('customer.orders.download-file', {customerOrder: this.order.id, file: 'invoice', preview: 'preview'})" target="_blank" class="btn-primary px-2.5 py-1.5">Download</a>
                        </div>

                        <file-input required :errors="$page.props.errors.invoice_file" v-model="invoice.invoice_file"></file-input>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="mt-4 sm:border-t sm:border-gray-200 sm:pt-5">
              <div role="group">
                <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-baseline">
                  <div>
                    <div class="text-base leading-6 font-medium text-gray-900 sm:text-sm sm:leading-5 sm:text-gray-700">
                      Lock
                    </div>
                  </div>
                  <div class="mt-4 sm:mt-0 sm:col-span-2">
                    <div class="relative flex items-start">
                      <div class="absolute flex items-center h-5">
                        <input id="lock" v-model="invoice.lock" type="checkbox" class="form-checkbox h-4 w-4 text-indigo-600 transition duration-150 ease-in-out" />
                        <div v-if="$page.props.errors.lock" class="form-error">{{ $page.props.errors.lock[0] }}</div>
                      </div>
                      <div class="pl-7 text-sm leading-5">
                        <label for="lock" class="font-medium text-gray-700">Lock the order</label>
                        <div class="text-xs text-gray-500">Locking will prevent from further modification to the order.</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="mt-4 flex justify-end">
              <loading-button key="invoice-submit" :loading="updatingInvoice" class="btn-primary">Update</loading-button>
            </div>
          </form>
        </modal>
        <!-- end of invoice modal -->

        <!-- remove items modal -->
        <modal :show="modals.removeItem && can_update_order_items && orderIsCompleted === false" @closeModal="modals.removeItem = false">
          <div class="px-4 py-5 sm:p-6">
            <h3 class="text-lg leading-6 font-medium text-gray-900 mb-2">
              Confirm removal of items
            </h3>
            <p class="text-sm text-gray-500 leading-5">Are you sure, you want to remove the following items? Once removed this action cannot be undone.</p>

            <table class="min-w-full mt-2">
              <thead>
              <tr>
                <th class="px-2 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
                  Item Code
                </th>
                <th class="px-2 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
                  Unit
                </th>
                <th class="px-2 py-3 border-b border-gray-200 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider">
                  Qty Ordered
                </th>
              </tr>
              </thead>
              <tbody class="bg-white">
              <tr v-for="(value, item_id) in checkedItems" v-if="value === true" :key="`removal-${item_id}`">
                <td class="px-2 py-4 whitespace-nowrap border-b border-gray-200 text-sm leading-5 font-medium text-gray-900">
                  {{ selectedItem(item_id).item_code }}
                </td>
                <td class="px-2 py-4 whitespace-nowrap border-b border-gray-200 text-sm leading-5 text-gray-900">
                  {{ selectedItem(item_id).unit }}
                </td>
                <td class="px-2 py-4 whitespace-nowrap border-b border-gray-200 text-sm leading-5 text-gray-900">
                  {{ selectedItem(item_id).quantity }}
                </td>
              </tr>
              </tbody>
            </table>

            <div class="mt-4 flex justify-end">
              <loading-button :loading="removingItems" @click="removeItems" class="btn-red">Remove</loading-button>
            </div>

          </div>
        </modal>
        <!-- end of remove items modal -->


        <!-- cancel modal -->
        <modal :show="modals.cancel" @closeModal="modals.cancel = false">
          <form @submit.prevent="cancelOrder">
            <div class="px-4 py-5 sm:p-6">
              <h3 class="text-lg leading-6 font-medium text-gray-900">
                Cancel Order
              </h3>
              <div>
                <select-input v-model="cancelOrderForm.reason_type" class="mt-2"  label="Please choose a reason to cancel">
                  <option v-for="(label, copt) in cancellationOptions" :value="copt">{{ label }}</option>
                  <option value="other">Other</option>
                </select-input>
              </div>
                <div id="other-reason" v-if="cancelOrderForm.reason_type === 'other'" class="mt-2">
                    <textarea-input v-model="cancelOrderForm.reason_details"  label="If Other, please specify the reason">
                    </textarea-input>
                </div>
              <div class="mt-2 max-w-xl text-sm leading-5 text-gray-500">
                <p>
                 Confirm to cancel this order, this action cannot be undone.
                </p>
              </div>
              <div class="mt-5">
                <loading-button type="submit" :loading="cancelling" class="inline-flex items-center justify-center px-4 py-2 border border-transparent font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-50 focus:outline-none focus:border-red-300 focus:shadow-outline-red active:bg-red-200 transition ease-in-out duration-150 sm:text-sm sm:leading-5">
                  Cancel Order
                </loading-button>
              </div>
            </div>
          </form>
        </modal>
        <!-- end of cancel modal -->

      </div>
      <!-- end of right col -->
    </div>
  </div>
</template>

<script>
  import Layout from '@/Shared/Admin/Layout'
  import SelectInput from '@/Shared/SelectInput'
  import LoadingButton from '@/Shared/LoadingButton'
  import FormInput from '@/Shared/Form/FormInput'
  import TextInput from '@/Shared/TextInput'
  import FileInput from '@/Shared/FileInput'
  import Modal from '@/Shared/Modal'
  import TextareaInput from '@/Shared/TextareaInput'
  import DateInput from '@/Shared/DateInput'
  import Icon from '@/Shared/Icon'
  import OrderHistoryOld from './OrderHistoryOld'
  import AddItemModal from './AddItemModal'
  import OrderFulfillmentDetails from './OrderFulfillmentDetails'
  import AvailableQtyBadge from '@/Shared/AvailableQtyBadge'
  import DeliveryToBoat from './DeliveryToBoat'
  import {Inertia} from '@inertiajs/inertia'
  import DeliveryNotes from "./Partials/DeliveryNotes/DeliveryNotes.vue";

  export default {
  name: 'Show',
  components: {
    DeliveryNotes,
    AvailableQtyBadge,
    OrderFulfillmentDetails,
    AddItemModal,
    OrderHistoryOld,
    Icon,
    DateInput,
    TextareaInput,
    DeliveryToBoat,
    Modal, FileInput, TextInput, FormInput, LoadingButton, SelectInput},
  metaInfo: { title: 'Customer Orders' },
  layout: Layout,

  props: {
    order: Object,
    order_total_price: Number,
    warehouses: Object,
    teams: Array,
    isCredit: Boolean,
    locations: Object,
    customerAccountLocation: String,
    vesselLocations: Object,
    enableQuoatingWhenNotApproved: Boolean,
    paymentMethods: Array,
    warehouseList: Array,
    isSuperAdmin: Boolean,
    duplicateItems: [Object, Array],
    orderIsCompleted: Boolean,
    user_warehouses: [Array],
    cancellationOptions: [Object],
  },

  data() {
    return {
      handover_wcode: null,
      total_price: this.order_total_price,
      // items available quantity details
      available_qty: {
        loaded: false,
        items: {}
      },
      viewing_handover_list_id: null,
      can_update_order_items: false,
      can_update_order_item_location: false,
      form: {},

      cancelOrderForm: {
        reason_type: null,
        reason_details: null
      },

      updateItems: [],

      require_payment_slip: !this.order.customer_payment_transfer_slip_image_path,
      payment_received_at: this.order.payment_received_at,
      payment_transfer_at: this.order.customer_payment_transfer_at,
      payment_slip: null,
      payment_method: this.order.payment_method ? this.order.payment_method : 'transfer', // transfer is set as default payment method

      checkedItems: {},

      quote: {
        quotation_ref: this.order.quotation_ref,
        quotation_amount: this.order.quotation_amount,
        quotation_file: null,
        advance_payment: this.order.advance_payment,
      },

      invoice: {
        quotation_customer_approved: this.order.quotation_customer_approved,
        invoice_ref: this.order.invoice_ref,
        invoice_amount: this.order.invoice_amount,
        invoice_file: null,
        lock: true
      },

      modals: {
        quotation: false,
        invoice: false,
        cancel: false,
        addItem: false,
        removeItem: false,
        updateSelectedItems: false,
      },

      unlocking: false,
      orderUpdating: false,
      updatingQuotation: false,
      updatingInvoice: false,
      paymentUpdating: false,
      cancelling: false,
      removingItems: false
    }
  },

    beforeMount() {
      if(this.order.status == 'open') {

        this.order.items.forEach((item)=> {
          this.fetchAvailableQty(item)
        })
      }
    },

    mounted() {
      this.setOrderDetails(this.order)

      if(['open'].includes(this.order.status)) {
        this.can_update_order_items = true
        this.can_update_order_item_location = true

      } else {
        //check if the user is admin
        if(this.$page.props.auth.user.role.super_role) {
          this.can_update_order_items = true
        } else {
          this.can_update_order_items = false
          this.can_update_order_item_location = this.canAdmin('update-order-item-location')
        }

      }
    },

    watch: {
      order(newOrder) {
        this.setOrderDetails(newOrder)
      },
    },

    beforeUpdate() {
    if (this.$page.props.errors.quotation_amount || this.$page.props.errors.quotation_ref || this.$page.props.errors.quotation_file) {
      this.modals.quotation = true
    }

    if (this.$page.props.errors.invoice_amount || this.$page.props.errors.invoice_ref || this.$page.props.errors.invoice_file) {
      this.$set(this.modals, 'invoice', true)
      // this.modals.invoice = true
    }

    if (this.$page.props.errors.date) {
      this.modals.generateHandoverList = true
    }
  },

  methods: {
    setOrderDetails(order) {
      this.form = {
        customer_to_collect: order.customer_to_collect,
        delivery_address: order.delivery_address,
        delivery_location: order.delivery_location || this.customerAccountLocation,
        customer_handover_date: order.customer_handover_date,
        vessel: order.vessel,
        vessel_location: order.vessel_location,
        boat_id: order.boat_id,
        delivery_contact_number: order.delivery_contact_number,
        customer_contact_number: order.customer_contact_number,
        remarks: order.remarks,
        items: order.items.map(itm => {
          if (itm.quantity_approved === null) {
            itm.quantity_approved = itm.quantity;
          }
          return itm;
        }),
      }
    },
    printOrderUrl(order) {
      if(this.handover_wcode) {
        return '/handover-list/print/' + order.id  + '?warehouse_only=' + this.handover_wcode;
      }
      return '/handover-list/print/' + order.id ;
    },

    confirmQuotation() {
      this.orderUpdating = true;
      this.$inertia.post(this.route('customer.orders.confirm-quotation'), {
        orderId: this.order.id
      },{
        onSuccess : () => this.orderUpdating = false
      })
    },

    createQuotation()
    {
      this.orderUpdating = true;
      this.$inertia.post(this.route('customer.orders.create-quotation'), {
        orderId: this.order.id
      },{
        onSuccess : () => this.orderUpdating = false
      })
    },

    createCreditInvoice()
    {
      this.orderUpdating = true;
      this.$inertia.post(this.route('customer.orders.make-credit-invoice'), {
        orderId: this.order.id
      },{
        onSuccess : () => this.orderUpdating = false
      })
    },

    updateSelectedBoatID(boatSelected) {
      this.form.boat_id = boatSelected.id
      this.form.vessel = boatSelected.name
    },

    fetchAvailableQty(item) {
      this.available_qty.loaded = false;
      this.$http.get(this.route('ajax.order_item.quantity',{
        order_id:item.order_id, item_code:item.item_code, unit: item.unit, warehouse_id: item.warehouse_id
      })).then((response) => {

        let existing = this.available_qty.items[response.data.item_code] || {}
        this.available_qty.items = {
          ...this.available_qty.items,
          [response.data.item_code] : {
            ...existing,
            [response.data.unit] : response.data,
          }
        };
        this.available_qty.loaded = true;
      })
    },

    validateQty(item, value) {
      let qty = Math.round(value)
      if (item.quantity_approved !== qty) {
        item.quantity_approved = qty;
      }
    },

    updateItemPrice(item, evt) {
      let original_price = item.price_total;

      if(item.quantity_approved !== "" || item.quantity_approved !== NaN) {
        item.price_total = item.price * item.quantity_approved;

        let diff = item.price_total - original_price;

        this.total_price = this.total_price + diff;
      }
    },

    openItemsUpdateModal() {
      if(Object.keys(this.checkedItems).length > 0) {
        Object.keys(this.checkedItems).forEach((item_id) => {
          if(this.checkedItems[item_id]) {
            let productItem = this.selectedItem(item_id);
            this.updateItems.push({...productItem})
          }
        })
        this.modals.updateSelectedItems = true;
      }
    },

    dismissUpdateItemsModal() {
      this.updateItems = [];
      this.modals.updateSelectedItems = false;
    },

    handleItemsUpdatedInOrder() {
      console.log('handleItemsUpdatedInOrder method fired');
      const payload = {
        order_id: this.order.id,
        items: this.updateItems.map(item => {
          return {
            id: item.id,
            item_code: item.item_code,
            quantity_approved: item.quantity_approved,
            unit_price: item.unit_price,
            discount_applied: item.discount_applied,
            price_total: item.unit_price * item.quantity_approved,
          }
        })
      }
      this.$inertia.post(this.route('ajax.customer.orders.update-items-in-order', {customerOrder: payload.order_id}), payload, {
        onSuccess: () => {
          this.$inertia.reload({
            only: ['order', 'order_total_price'],
            onSuccess: () => {
              this.form.items = this.order.items.map(itm => {
                if (itm.quantity_approved === null) {
                  itm.quantity_approved = itm.quantity;
                }
                return itm;
              });

              this.form.items.forEach((item)=> {
                this.fetchAvailableQty(item)
              })

              this.total_price = this.order_total_price;
            }
          });
        },
        onFinish: () => {
          this.dismissUpdateItemsModal();
          this.checkedItems = {}
        }
      })
      // handle posting updates to server side and reload the items list
    },

    itemAddedToOrder() {
      this.$inertia.reload({
        only: ['order', 'order_total_price', 'warehouses', 'locations', 'warehouse-list', 'duplicateItems'],
        onSuccess: () => {
          this.form.items = this.order.items.map(itm => {
            if (itm.quantity_approved === null) {
              itm.quantity_approved = itm.quantity;
            }
            return itm;
          });

          this.form.items.forEach((item)=> {
            this.fetchAvailableQty(item)
          })

          this.total_price = this.order_total_price;
          this.modals.addItem = false;
        },
        onFinish: () => {

        }
      })


    },


    removeItems() {
      if(this.canAdmin('remove-items-from-an-order')) {
        this.removingItems = true;
        this.$inertia.post(this.route('customer.orders.remove-items', this.order.id), {items: this.checkedItems}, {
          preserveState: false
        },{
          onSuccess : () => this.removingItems = false
        })
      }
    },

    closeQuotationModal(){
      this.$page.props.errors = {};
      this.modals.quotation = false
    },

    closeInvoiceModal() {
      this.$page.props.errors = {};
      this.modals.invoice = false
    },

    getWarehouseListFor(item_code) {
      let items = [];

      if(this.warehouses[item_code]) {

        this.warehouses[item_code].forEach((item) => {
          items.push({
            id: item.id,
            name: item.name
          })
        });
      }

      return items;
    },

    revertStatusToOpen() {
      this.orderUpdating = true;
      this.$inertia.post(this.route('customer.orders.revert_status', this.order.id), {
        orderId: this.order.id
      },{
        onSuccess : () => this.orderUpdating = false
      })
    },

    updateOrder() {

      if(this.canAdmin('update-orders') || this.canAdmin('update-order-item-location'))
      {
        this.orderUpdating = true
        let data = this.form;
        data.customer_to_collect = data.customer_to_collect == true ? true : false

        Inertia.put(this.route('customer.orders.update', this.order.id), data,{
          onFinish : () => {
            this.orderUpdating = false
          }
        })
      }

    },

    approveOrder() {

      if(this.canAdmin('approve-orders')) {
        this.orderUpdating = true
        let data = this.form;
        data.customer_to_collect = data.customer_to_collect == true ? true : false

        this.$inertia.put(this.route('customer.orders.update', {customerOrder: this.order.id, approval: true}), data,{
          onFinish : () => this.orderUpdating = false
        })
      }

    },

    updateQuote() {

      this.updatingQuotation = true;
      let formData  = new FormData();
      formData.append('quotation_ref', this.quote.quotation_ref);
      formData.append('quotation_amount', this.quote.quotation_amount);
      if (this.quote.quotation_file) {
        formData.append('quotation_file', this.quote.quotation_file);
      }

      if (this.isCredit) {
        formData.append('advance_payment', this.quote.advance_payment == true ? 1 : 0)
      }

      this.$inertia.post(this.route('customer.orders.update-quote', this.order.id), formData,{
        onSuccess : () => {
          this.modals.quotation = false
        },
        onFinish : () => {
          this.updatingQuotation = false
        }
      })
    },

    updateInvoice() {

      this.updatingInvoice = true;
      let formData  = new FormData();
      formData.append('quotation_customer_approved', this.invoice.quotation_customer_approved == true ? 1 : 0);
      formData.append('invoice_ref', this.invoice.invoice_ref);
      formData.append('invoice_amount', this.invoice.invoice_amount);
      if (this.invoice.invoice_file) {
        formData.append('invoice_file', this.invoice.invoice_file);
      }
      formData.append('lock', this.invoice.lock == true ? 1 : 0)

      this.$inertia.post(this.route('customer.orders.update-invoice', this.order.id), formData,{
        onSuccess : () => {
          this.updatingInvoice = false
          this.modals.invoice = false
        }
      })
    },

    updatePayment() {
      this.paymentUpdating = true;


      let formData = new FormData();
      formData.append('payment_transfer_at', this.payment_transfer_at)
      formData.append('payment_received_at', this.payment_received_at)
      formData.append('payment_method', this.payment_method)
      formData.append('require_payment_slip', this.require_payment_slip === true ? 1 : 0)

      if (this.payment_slip) {
        formData.append('payment_slip', this.payment_slip);
      }

      this.$inertia.post(this.route('customer.orders.update-payment', { customerOrder: this.order.id }),  formData,{
        onFinish : () => this.paymentUpdating = false
      })
    },

    cancelOrder() {
      this.cancelling = true
      this.$inertia.post(this.route('customer.orders.cancel', this.order.id), {
          reason_type: this.cancelOrderForm.reason_type,
          reason_details: this.cancelOrderForm.reason_type === 'other' ? this.cancelOrderForm.reason_details : this.cancellationOptions[this.cancelOrderForm.reason_type]
      }, {
        onError: () => {
          window.alert("You are required to provide valid reason for the cancellation");
        },
        onSuccess: () => {
          this.cancelling = false;
          this.modals.cancel = false;
          this.cancelOrderForm = {
              reason_type: null,
              reason_details: null
          }
        }
      })
    },

    unlockOrder() {
      this.unlocking = true;
      this.$inertia.post(this.route('customer.orders.unlock', this.order.id),{
        onFinish : () => this.unlocking = false
      })
    }
  },

  computed: {
    isLocked() {
      if(this.canAdmin('update-orders'))
      {
        return this.order.lock;
      }
      return true;
    },

    totalOrderPrice() {

    },

    isItemLocked() {
      return function (item) {
        return item.completed_at !== null || item.handovers.length > 0;
      }
    },

    canChangeItemLocation(item) {
      return function () {
        return this.canAdmin('update-order-item-location') && item.completed_at !== null;
      }
    },

    selectedItem() {
      return function(item_id) {
        return this.form.items.find(itm => itm.id == item_id)
      }
    },

    disableRemoveButton() {
      if (Object.keys(this.checkedItems).length) {
        let values = Object.values(this.checkedItems)
        return values.filter(itm => itm === true).length === 0;
      }
      return true
    }
  }
}
</script>

